// Define the PikErrorLike interface
export interface PikErrorLike {
  type: string; // URL of the error type (e.g., "https://pik.sa/errors/orders/business-erorr")
  title: string; // Error title (e.g., "Business Error")
  status: number; // HTTP status code (e.g., 400)
  detail: string; // Detailed error message (e.g., "Invalid coupon. Please check the code and try again")
  traceId?: string; // Optional traceId for tracking the error (e.g., "0HN6U5FHFNSI9:00000002")
  errors?: Record<string, unknown>; // Optional additional errors
}

// Utility function to check if a value is an object
export const isObject = (object: unknown): object is Record<string, unknown> => {
  return typeof object === 'object' && object !== null && !Array.isArray(object);
};

// Type guard function to check if an unknown value is a PikErrorLike
export const isPikError = (error: unknown): error is PikErrorLike => {
  // Check if the error is an object
  if (!isObject(error)) return false;

  // Type assertion: Narrow down to Record<string, unknown>
  const errorObject = error as Record<string, unknown>;

  // Check if the necessary fields for PikErrorLike exist in the object
  return (
    typeof errorObject.type === 'string' &&
    typeof errorObject.title === 'string' &&
    typeof errorObject.status === 'number' &&
    typeof errorObject.detail === 'string'
  );
};

// Recursively checks if an object or any of its prototypes is an Error
export function findError<T extends object>(error: T): boolean {
  if (Object.prototype.toString.call(error) === '[object Error]') {
    return true;
  }

  const prototype = Object.getPrototypeOf(error) as T | null;
  return prototype === null ? false : findError(prototype);
}
